<template>
  <div>
    <PageAllow controller="FinanceBills" action="edit" />
    <Breadcrumbs :items="items" />
    <v-slide-x-transition>
      <v-sheet class="transparent-background-color">
        <v-row dense class="mx-5">
          <v-col cols="12" sm="12" md="4">
            <v-row dense align="center" class="pa-1">
              <SearcherFinanceMaterialCategory v-model="categorySelected" />
            </v-row>
          </v-col>
          <v-col cols="12" sm="12" md="8" align-self="center">
            <v-row dense align="center" class="search-controls">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    v-on="on"
                    small
                    text
                    class="px-0 py-0 caption font-weight-bold grey--text"
                    color="primary"
                    @click.stop="onSearch()"
                  >
                    <v-icon small class="pe-1">fa-search</v-icon>
                    {{ $t("Search.search") }}
                  </v-btn>
                </template>
                <span>{{ $t("Search.searchTip") }}</span>
              </v-tooltip>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    v-on="on"
                    small
                    text
                    class="caption font-weight-bold grey--text"
                    @click.stop="resetSearch()"
                  >
                    <v-icon x-small class="pe-1">fa-trash-alt</v-icon>
                    {{ $t("Search.reset") }}
                  </v-btn>
                </template>
                <span>{{ $t("Search.resetTip") }}</span>
              </v-tooltip>
              <v-divider vertical />
              <v-btn
                class="caption font-weight-bold grey--text"
                :class="
                  $vuetify.theme.dark ? 'text--lighten-1' : 'text--darken-1'
                "
                small
                text
                @click.stop="$store.commit('toggleSearchPanel')"
              >
                {{ $t("Search.pro") }}
                <v-icon small class="ps-1">{{
                  $store.state.showSearchPanel ? "fa-angle-up" : "fa-angle-down"
                }}</v-icon>
              </v-btn>
              <v-spacer />

              <v-btn
                small
                color="success"
                dark
                class="ms-1"
                @click.stop="save()"
              >
                <v-icon x-small class="pe-1">fas fa-save</v-icon>
                {{ $t("financeBillEdit.save") }}
              </v-btn>
            </v-row>
          </v-col>
        </v-row>
        <v-expansion-panels
          v-model="$store.getters.panels"
          multiple
          flat
          accordion
        >
          <v-expansion-panel class="transparent-background-color">
            <v-expansion-panel-content>
              <v-row dense align="center">
                <v-col cols="12" sm="6" md="4">
                  <SearcherKeyword
                    ref="keyword"
                    v-model="options.keyword"
                    @text="KeyWord = $event"
                    :label="$t('financeBillEdit.keyword')"
                  />
                </v-col>
              </v-row>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>

        <div class="mx-4 mb-4">
          <v-data-table
            :headers="headers"
            :items="editItems"
            :loading="loading"
            :options.sync="options"
            hide-default-footer
            disable-pagination
            multi-sort
            class="elevation-2 px-4 pt-1"
          >
            <template v-slot:top>
              <v-card-text>
                <v-row dense>
                  <v-col cols="12" sm="6" md="4" class="text-no-wrap">
                    <span class="font-weight-bold me-2">
                      {{ $t("financeBillDetail.obj") }}{{ $t("Global.colon") }}
                    </span>
                    <span v-if="bill.bill_obj_type == 1">
                      {{
                        finance_bill.shop.shop_name +
                        "(" +
                        finance_bill.shop.shop_code +
                        ")"
                      }}
                    </span>
                    <span v-if="bill.bill_obj_type == 2">
                      {{
                        finance_bill.supplier.supplier_name +
                        "(" +
                        finance_bill.supplier.supplier_code +
                        ")"
                      }}
                    </span>
                    <span v-if="bill.bill_obj_type == 3">
                      {{
                        finance_bill.stock.name +
                        "(" +
                        finance_bill.stock.code +
                        ")"
                      }}
                    </span>
                  </v-col>
                  <v-col cols="12" sm="6" md="4" v-if="bill.bill_obj_type == 1">
                    <span class="font-weight-bold me-2" v-if="finance_bill.finance_bill_type == 101">
                      {{ $t("financeBillDetail.org_code") }}{{ $t("Global.colon") }}
                    </span>
                    <span class="font-weight-bold me-2" v-else-if="finance_bill.finance_bill_type == 102">
                      {{ $t("financeBillDetail.return_order_code") }}{{ $t("Global.colon") }}
                    </span>
                    <a
                      v-if="finance_bill.finance_bill_type == 101"
                      style="text-decoration: none"
                      :href="'/#/orders/' + bill.org_id"
                    >
                      {{ bill.org_code }}
                    </a>
                    <a
                      v-if="finance_bill.finance_bill_type == 102"
                      style="text-decoration: none"
                      :href="'/#/return-orders/' + bill.org_id"
                    >
                      {{ bill.org_code }}
                    </a>
                    <v-chip
                      v-if="bill.cancel_flg"
                      small
                      label
                      outlined
                      color="danger"
                      class="ms-1 my-n1"
                      >{{ $t("CRUD.cancelled") }}
                    </v-chip>
                  </v-col>
                  <v-col cols="12" sm="6" md="4" v-if="bill.bill_obj_type == 2">
                    <span class="font-weight-bold me-2" v-if="finance_bill.finance_bill_type == 201">
                      {{ $t("financeBillDetail.purchase_bill_code")}}{{ $t("Global.colon") }}
                    </span>
                    <span class="font-weight-bold me-2" v-else-if="finance_bill.finance_bill_type == 202">
                      {{ $t("financeBillDetail.return_purchase_bill_code")}}{{ $t("Global.colon") }}
                    </span>
                    <a
                      style="text-decoration: none"
                      :href="'/#/receipts/' + bill.id + '?time_type=1'"
                    >
                      {{ bill.bill_code }}
                    </a>
                  </v-col>
                  <v-col cols="12" sm="6" md="4" v-if="bill.bill_obj_type == 2">
                    <span class="font-weight-bold me-2" v-if="finance_bill.finance_bill_type == 201">
                      {{ $t("financeBillDetail.in_code") }}{{ $t("Global.colon") }}
                    </span>
                    <span class="font-weight-bold me-2" v-else-if="finance_bill.finance_bill_type == 202">
                      {{ $t("financeBillDetail.out_code") }}{{ $t("Global.colon") }}
                    </span>
                    <a
                      v-if="finance_bill.finance_bill_type == 201"
                      style="text-decoration: none"
                      :href="'/#/stock-orders-in/' + bill.org_id"
                    >
                      {{ bill.org_code }}
                    </a>
                    <a
                      v-if="finance_bill.finance_bill_type == 202"
                      style="text-decoration: none"
                      :href="'/#/stock-orders-out/' + bill.org_id"
                    >
                      {{ bill.org_code }}
                    </a>
                  </v-col>
                  <v-col cols="12" sm="6" md="4" v-if="bill.bill_obj_type == 1">
                    <span class="font-weight-bold me-2">
                      {{ $t("financeBillDetail.sum_price") }}
                      {{ $t("Global.colon") }}
                    </span>
                    <span
                      >{{ $t("Global.money")
                      }}{{ total_price | formatMoney }}</span
                    >
                    <span class="red--text">
                      (
                      {{
                        $t("financeBillDetail.fee") +
                        $t("Global.colon") +
                        $t("Global.money")
                      }}</span
                    >
                    <span class="red--text">{{ fee | formatMoney }} ) </span>
                  </v-col>
                  <v-col cols="12" sm="6" md="4" v-if="bill.bill_obj_type == 2">
                    <span class="font-weight-bold me-2">
                      {{ $t("financeBillDetail.sum_price_tax") }}
                      {{ $t("Global.colon") }}
                    </span>
                    <span
                      >{{ $t("Global.money")
                      }}{{ total_price | formatMoney }}</span
                    >
                    <span class="red--text">
                      (
                      {{
                        $t("financeBillDetail.fee") +
                        $t("Global.colon") +
                        $t("Global.money")
                      }}</span
                    >
                    <span class="red--text">{{ fee | formatMoney }} ) </span>
                  </v-col>
                  <v-col cols="12" sm="6" md="4" v-if="bill.bill_obj_type == 3">
                    <span class="font-weight-bold me-2" v-if="finance_bill.finance_bill_type == 301">
                      {{ $t("financeBillDetail.requisitions_bill_code")}}{{ $t("Global.colon") }}
                    </span>
                    <span class="font-weight-bold me-2" v-if="finance_bill.finance_bill_type == 302">
                      {{ $t("financeBillDetail.requisitions_pay_bill_code")}}{{ $t("Global.colon") }}
                    </span>
                    <a
                      style="text-decoration: none"
                      :href="'/#/receipts/' + bill.id + '?time_type=1'"
                    >
                      {{ bill.bill_code }}
                    </a>
                  </v-col>
                  <v-col cols="12" sm="6" md="4" v-if="bill.bill_obj_type == 3">
                    <span class="font-weight-bold me-2">
                      {{ $t("financeBillDetail.requisitions_code") }}{{ $t("Global.colon") }}
                    </span>
                    <a
                      style="text-decoration: none"
                      :href="'/#/requisitions/' + bill.org_id"
                    >
                      {{ bill.org_code }}
                    </a>
                  </v-col>
                </v-row>
                <v-col cols="12" sm="6" md="4" v-if="bill.bill_obj_type == 3">
                  <span class="font-weight-bold me-2">
                    {{ $t("financeBillDetail.sum_price_tax") }}
                    {{ $t("Global.colon") }}
                  </span>
                  <span>
                    {{ $t("Global.money") }}
                    {{ total_price | formatMoney }}
                  </span>
                  <span class="red--text">
                    ({{
                        $t("financeBillDetail.fee") +
                        $t("Global.colon") +
                        $t("Global.money") }}
                  </span>
                  <span class="red--text">{{ fee | formatMoney }} ) </span>
                </v-col>
                <!-- settings &&
                    settings.bill_credential_type &&
                    settings.bill_credential_type.shop_bill == '12' -->
                <v-row dense class="px-1" v-if="bill.bill_obj_type == 1">
                  <v-spacer />
                  <v-col cols="12" sm="12" md="4">
                    <span class="transparent--text font-weight-bold me-2">
                      {{ $t("financeBillDetail.sum_price") }}
                      {{ $t("Global.colon") }}
                    </span>
                    <span class="transparent--text"
                    >{{ $t("Global.money")
                      }}{{ total_price | formatMoney }}</span
                    >
                    <span class="red--text">
                      (
                      {{
                        $t("ReceiptDetail.service_charge") + $t("Global.colon") + $t("Global.money")
                      }}</span
                    >
                    <span class="red--text">{{ bill.service_charge | formatMoney }} ) </span>
                  </v-col>
                </v-row>
                <v-row dense class="px-1" v-if="bill.bill_obj_type == 1">
                  <v-spacer />
                  <v-col cols="12" sm="12" md="4">
                    <span class="transparent--text font-weight-bold me-2">
                      {{ $t("financeBillDetail.sum_price") }}
                      {{ $t("Global.colon") }}
                    </span>
                    <span class="transparent--text"
                      >{{ $t("Global.money")
                      }}{{ total_price | formatMoney }}</span
                    >
                    <span class="red--text">
                      (
                      {{
                        $t("ReceiptDetail.logistics_fee") + $t("Global.colon")
                      }}</span
                    >
                    <span class="red--text">
                      <span style="width: 20px">
                        <v-text-field
                          v-model="logistics_fee"
                          :placeholder="
                            String(
                              $numberFormatter.formatTwoDot(logistics_fee, 2)
                            )
                          "
                          single-line
                          dense
                          hide-details
                          type="number"
                          min="0"
                          :prefix="$t('Global.money')"
                          @blur="
                            logistics_fee =
                              $numberFormatter.formatWithDotNoMoreThan(
                                Number(logistics_fee),
                                2
                              )
                          "
                          style="width: 60px"
                          class="d-inline-flex"
                        />
                      </span>
                      )
                    </span>
                  </v-col>
                </v-row>
                <v-row dense class="px-1" v-if="finance_bill.finance_bill_type == 101 && settings && settings.order_discount">
                  <v-spacer />
                  <v-col cols="12" sm="12" md="4">
                    <span class="transparent--text font-weight-bold me-2">
                      {{ $t("financeBillDetail.sum_price") }}
                      {{ $t("Global.colon") }}
                    </span>
                    <span class="transparent--text">
                      {{ $t("Global.money") }}{{ total_price | formatMoney }}
                    </span>
                    <span class="red--text">
                      (
                      {{
                        $t("ReceiptDetail.order_discount") + $t("Global.colon")
                      }}
                    </span>
                    <span class="red--text">
                      <span style="width: 20px">
                        <v-text-field
                          v-model="order_discount"
                          :placeholder="String($numberFormatter.formatTwoDot(order_discount, 2))"
                          single-line
                          dense
                          hide-details
                          type="number"
                          min="0"
                          :prefix="$t('Global.money')"
                          @blur="
                            order_discount =
                              $numberFormatter.formatWithDotNoMoreThan(
                                Number(order_discount) <= total_price ? Number(order_discount) : total_price,
                                2
                              )
                          "
                          style="width: 60px"
                          class="d-inline-flex"
                        />
                      </span>
                      )
                    </span>
                  </v-col>
                </v-row>
              </v-card-text>
              <v-divider></v-divider>
            </template>
            <template v-slot:header.actions="{ header }">
              {{ header.text }}
              <v-icon x-small>fa-sliders-h</v-icon>
            </template>
            <template v-slot:item.content="{ item }">
              <span>{{ item.content }}</span>
              <br />
              <span class="caption secondary--text">
                {{ $t("Material.Fields.standard") }}{{ $t("Global.colon")}}
                <span v-if="item.material && item.material.standard_name && item.material.standard_name.length > 12" :title="item.material.standard_name" style="cursor: pointer">
                  {{ item.material.standard_name.slice(0, 12) + "......" }}
                </span>
                <span v-else>
                  {{ item.material.standard_name }}
                </span>
              </span>
            </template>
            <!-- 单价 -->
            <template v-slot:item.price="{ item }">
              <v-text-field
                v-model="item.price"
                :placeholder="
                  String($numberFormatter.formatTwoDot(item.price, 2))
                "
                single-line
                dense
                hide-details
                type="number"
                min="0"
                :prefix="$t('Global.money')"
                @blur="
                  item.price = $numberFormatter.formatWithDotNoMoreThan(
                    Number(item.price),
                    2
                  )
                   item.total = $numberFormatter.formatWithDotNoMoreThan(
                    Number(item.num * item.price),
                    2
                  )
                "
              />
            </template>
            <!-- 数量 -->
            <template v-slot:item.num="{ item }">
              <v-text-field
                v-model="item.num"
                single-line
                dense
                hide-details
                type="number"
                min="0"
                @blur="
                  item.num = $numberFormatter.formatWithDotNoMoreThan(
                    Number(item.num),
                    2
                  )
                   item.total = $numberFormatter.formatWithDotNoMoreThan(
                    Number(item.num * item.price),
                    2
                  )
                "
              />
            </template>
            <!-- 小计 -->
            <template v-slot:item.total="{ item }">
              <v-text-field
                v-model="item.total"
                :placeholder="
                  String($numberFormatter.formatTwoDot(item.total, 2))
                "
                single-line
                dense
                hide-details
                type="number"
                min="0"
                :prefix="$t('Global.money')"
                @blur="
                  item.total = $numberFormatter.formatWithDotNoMoreThan(
                    Number(item.total),
                    2
                  );
                  item.price = (item.num && item.num > 0) ? $numberFormatter.formatWithDotNoMoreThan(
                    Number(item.total / item.num),
                    2
                  ) : 0
                "
              />
            </template>
            <!-- 备注 -->
            <template v-slot:item.actions="{ item }">
              <v-text-field
                v-model="item.memo"
                single-line
                dense
                hide-details
                style="max-width: 120px"
              >
                <template v-slot:append>
                  <v-icon
                    x-small
                    @click.stop="
                      detail_memo_item = item;
                      memoDialog = true;
                    "
                    class="pt-2 pb-1 primary--text"
                  >
                    fa-edit
                  </v-icon>
                </template>
              </v-text-field>
            </template>
          </v-data-table>
        </div>
      </v-sheet>
    </v-slide-x-transition>

    <!-- 备注弹窗 -->
    <v-dialog v-model="memoDialog" max-width="480px">
      <v-card>
        <v-card-title class="mx-3">
          <span class="d-inline-block text-center" style="max-width: 360px">
            {{ $t("financeBillEdit.memo") }}
          </span>
          <v-spacer></v-spacer>
          <v-tooltip left>
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-bind="attrs" v-on="on" @click="memoDialog = false">
                fa-times
              </v-icon>
            </template>
            {{ $t("$vuetify.close") }}
          </v-tooltip>
        </v-card-title>
        <v-divider />
        <v-card-text class="px-4 pt-8">
          <v-textarea
            v-model="detail_memo_item.memo"
            outlined
            :label="$t('Order.DetailFields.memo')"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="secondary" outlined @click="memoDialog = false">
            {{ $t("$vuetify.close") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapState } from "vuex";
import i18n from "@/i18n";
import PageAllow from "@/components/PageAllow";
import Breadcrumbs from "@/components/Breadcrumbs";
import SearcherFinanceMaterialCategory from "@/components/plugins/SearcherFinanceMaterialCategory";
import SearcherKeyword from "@/components/plugins/SearcherKeyword";
import FinanceBills from "@/api/finance_bills";

const defaultOptions = {
  category_ids: null,
  keyword: "",
};

function getPageType() {
  let urlParts = document.URL.split('/');
  urlParts = urlParts[urlParts.length - 3]
  switch (urlParts) {
    default:
    case "finance-bills-order":
      return 101;
    case "finance-bills-return":
      return 102;
    case "finance-bills-purchase":
      return 201;
    case "finance-bills-purchaseReturn":
      return 202;
    case "finance-bills-requisition":
      return 301;
    case "finance-bills-requisitionPay":
      return 302;
  }
}

function getOptionPrefix() {
  switch (getPageType()) {
    default:
    case 101:
      return "finance-bills-order";
    case 102:
      return "finance-bills-return";
    case 201:
      return "finance-bills-purchase";
    case 202:
      return "finance-bills-purchaseReturn";
    case 301:
      return "finance-bills-requisition";
    case 302:
      return "finance-bills-requisitionPay";
  }
}

export default {
  data: () => ({
    options: JSON.parse(JSON.stringify(defaultOptions)),
    categorySelected: null,
    editItems: [],
    detail_memo_item: { memo: "" },
    memoDialog: false,
    bill: {},
    finance_bill: {},
    logistics_fee: 0,
    order_discount: 0,
    total_price: null,
    fee: null,
  }),
  components: {
    PageAllow,
    Breadcrumbs,
    SearcherFinanceMaterialCategory,
    SearcherKeyword,
  },
  computed: {
    items() {
      let items = [
        {
          text: i18n.t("Nav.home"),
          disabled: false,
          href: "/home",
        },
        {
          text: i18n.t("Nav.inanceReceipt"),
          disabled: true,
          href: "/finance-receipt",
        },
      ];
      if (this.$route.path.startsWith("/finance-bills-order")) {
        items.push({
          text: i18n.t("Nav.financeBillForOrder"),
          disabled: true,
          href: "/finance-bills-order",
        });
      } else if (this.$route.path.startsWith("/finance-bills-return")) {
        items.push({
          text: i18n.t("Nav.financeBillForReturn"),
          disabled: true,
          href: "/finance-bills-return",
        });
      } else if (this.$route.path.startsWith("/finance-bills-purchaseReturn")) {
        items.push({
          text: i18n.t("Nav.financeBillForPurchaseReturn"),
          disabled: true,
          href: "/finance-bills-purchaseReturn",
        });
      } else if (this.$route.path.startsWith("/finance-bills-purchase")) {
        items.push({
          text: i18n.t("Nav.financeBillForPurchase"),
          disabled: true,
          href: "/finance-bills-purchase",
        });
      } else if (this.$route.path.startsWith("/finance-bills-requisitionPay")) {
        items.push({
          text: i18n.t("Nav.financeBillForRequisitionPay"),
          disabled: true,
          href: "/finance-bills-requisitionPay",
        });
      } else if (this.$route.path.startsWith("/finance-bills-requisition")) {
        items.push({
          text: i18n.t("Nav.financeBillForRequisition"),
          disabled: true,
          href: "/finance-bills-requisition",
        });
      }
      items.push({
        text: i18n.t("Nav.financeBillEdit"),
        disabled: true,
      });
      items.push({
        text: this.finance_bill && this.finance_bill.finance_bill_code ? this.finance_bill.finance_bill_code : "",
        disabled: false,
        accent: true,
      });
      return items
    },
    headers() {
      return [
        {
          text: i18n.t("financeBillEdit.Fields.material_category"),
          align: "start",
          sortable: false,
          value: "material.material_category.name",
        },
        {
          text: i18n.t("financeBillEdit.Fields.material_code"),
          align: "start",
          sortable: false,
          value: "material.material_code",
        },
        {
          text: i18n.t("financeBillEdit.Fields.material_name"),
          align: "start",
          sortable: false,
          value: "content",
        },
        {
          text: i18n.t("financeBillEdit.Fields.num"),
          align: "start",
          sortable: false,
          value: "num",
          width: "120px",
        },
        {
          text: i18n.t("financeBillEdit.Fields.price"),
          align: "start",
          sortable: false,
          value: "price",
          width: "120px",
        },
        {
          text: i18n.t("financeBillEdit.Fields.sum"),
          align: "start",
          sortable: false,
          value: "total",
          width: "120px",
        },
        {
          text: i18n.t("TableHeaders.actions"),
          align: "start",
          sortable: false,
          value: "actions",
        },
      ];
    },

    ...mapState(["loading", "settings"]),
  },

  methods: {
    onSearch() {
      this.options = Object.assign(this.options, {
        keyword: this.KeyWord,
      });
      this.getFinanceBill();
    },
    resetSearch() {
      this.categorySelected = null;
      this.options = JSON.parse(JSON.stringify(defaultOptions));
    },

    getFinanceBill() {
      FinanceBills.fetchOne(this.$route.params.id, this.options).then(
        function (response) {
          if (response.data.status.isOK()) {
            if (response.data.finance_bill.bill.status != 1) {
              this.$router.push("/" + getOptionPrefix());
            }
            this.finance_bill = response.data.finance_bill;
            this.bill = response.data.finance_bill.bill;
            this.editItems = response.data.finance_bill.finance_bill_details.map((detail) => {
              if (detail.total <= 0 && detail.price > 0 && detail.num > 0) {
                detail.total = parseFloat(detail.price * detail.num).toFixed(2);
              }
              return detail
            });
            this.logistics_fee = response.data.finance_bill.logistics_fee;
            this.order_discount = response.data.finance_bill.order_discount;
          }
        }.bind(this)
      );
    },

    save() {
      // console.log(this);
      var data = [];
      var finance_bill_details = [];
      this.editItems.map((e) => {
        data.push({
          id: e.id,
          num: e.num,
          price: e.price,
          total: e.total,
          memo: e.memo,
        });
      });
      finance_bill_details.push(data);

      FinanceBills.edit(this.$route.params.id, {
        finance_bill_details: finance_bill_details[0],
        logistics_fee: this.logistics_fee,
        order_discount: this.order_discount
      }).then(
        function (response) {
          if (response.data.status.isOK()) {
            window.parent.parent.location.href = "/#/" + getOptionPrefix();
          }
        }.bind(this)
      );
    },
  },

  watch: {
    options: {
      deep: true,
      handler() {
        this.getFinanceBill();
      },
    },
    categorySelected: {
      handler() {
        // console.log(this.);
        this.options.category_ids = this.categorySelected
          ? this.categorySelected.id
          : null;
      },
    },
    editItems: {
      deep: true,
      handler() {
        var total_price = 0;
        this.editItems.forEach((item) => {
          total_price += Number(item.total)
        });
        total_price = parseFloat(total_price.toFixed(2));
        this.fee = total_price;
        this.total_price = Number(total_price) + Number(this.logistics_fee) + Number(this.bill.service_charge);
      },
    },
    logistics_fee: {
      deep: true,
      handler() {
        var total_price = 0;
        this.editItems.forEach((item) => {
          total_price += Number(item.total)
        });
        total_price = parseFloat(total_price.toFixed(2));
        this.fee = total_price;
        this.total_price = Number(total_price) + Number(this.logistics_fee) + Number(this.bill.service_charge);
      },
    },
  },
};
</script>

<style>
</style>